import { Component, OnInit } from '@angular/core';
import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import {
  BreadcrumbService,
  BreadcrumbItem,
} from 'src/app/services/breadcrumb/breadcrumb.service';
import Utils from 'src/app/helpers/utils';

@Component({
  selector: 'app-dashboard-groups',
  templateUrl: './dashboard-groups.component.html',
  styleUrls: ['./dashboard-groups.component.scss'],
})
export class DashboardGroupsComponent implements OnInit {
  permissionObj = {
    canAdd: true,
    canChange: true,
    canFilter: true,
    canInactive: true,
  };

  filterForm = this.formBuilder.group({
    name: [null, []],
    start_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    end_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
  });

  data: GroupUsers[] = [];
  count = 0;
  offset = 0;

  showFilterModal = false;
  loading = true;
  /**
   * Padrão de Filtros:
   * - hasFilters só deve ser true APÓS aplicar os filtros (método filterData())
   * - NÃO atualizar hasFilters no valueChanges do filterForm
   * - hasFilters deve ser atualizado apenas quando:
   *   1. filterData() executar com sucesso (após a requisição)
   *   2. resetForm() for chamado (define como false)
   *   3. toggleFilterModal() quando há filtros salvos no storage
   */
  hasFilters = false;
  appliedFilters: Array<{ label: string; value: string }> = [];

  // Propriedades para o warning modal
  showStatusModal = false;
  selectedGroup: any = null;
  isActive: boolean = false;
  results_per_page = 0;
  showLoadingSpinner = false;

  constructor(
    private api: ApiService,
    private toast: ToastService,
    public signupService: SignupService,
    private router: Router,
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    this.setPermissions();
    this.setBreadcrumb();
    this.getData();
  }

  async getData(page: number = 1) {
    this.loading = true;
    try {
      const filters: any = {};

      // Construir filtros a partir do formulário (se houver valores)
      const values = this.filterForm.value;
      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          // Verificar se o campo tem erro de validação antes de incluir
          const control = this.filterForm.get(key);
          if (control && control.valid) {
            let newValue = value;
            if (key === 'start_date' || key === 'end_date') {
              newValue = value.split('/').reverse().join('-');
            }
            filters[key] = newValue;
          }
        }
      });

      if (filters['start_date'] && !filters['end_date']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['end_date'] = atualDate;
      }

      if (filters['end_date'] && !filters['start_date']) {
        filters['start_date'] = '2000-01-01';
      }

      const { offset, results, count, results_per_page } = await this.api.get({
        route: 'api/v2/user-profile/',
        token: true,
        params: {
          page: page,
          type: 'list',
          ...filters,
        },
      });

      this.results_per_page = results_per_page;

      this.count = count;
      this.data = results;
      this.offset = offset;
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
    this.loading = false;
  }

  addGroup() {
    this.router.navigateByUrl('/app/permissions/groups/new');
  }

  editGroup(id: number) {
    this.router.navigateByUrl('/app/permissions/groups/update/' + id);
  }

  changePageData(page: number) {
    this.loading = true;
    this.getData(page);
  }

  resetForm() {
    this.loading = true;
    this.hasFilters = false;
    this.appliedFilters = [];
    this.filterForm.reset();
    this.getData();
  }

  generateFilterTags(formValues: any) {
    this.appliedFilters = [];

    const fieldLabels: { [key: string]: string } = {
      name: 'Perfil',
      start_date: 'Data inicial',
      end_date: 'Data final',
    };

    Object.entries(formValues).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        let displayValue = value;

        // Tratamento especial para datas (já vem formatado do formulário)
        if (key === 'start_date' || key === 'end_date') {
          displayValue = value;
        }

        if (fieldLabels[key]) {
          // Combinar label e valor no formato "label: valor"
          const combinedLabel = `${fieldLabels[key]}: ${String(displayValue)}`;
          this.appliedFilters.push({
            label: combinedLabel,
            value: String(displayValue),
          });
        }
      }
    });
  }

  toggleFilterModal() {
    this.showFilterModal = !this.showFilterModal;

    if (this.showFilterModal) {
      // Se houver filtros aplicados, restaurar no formulário
      if (this.hasFilters && this.appliedFilters.length > 0) {
        // Os valores já estão no formulário, apenas garantir que as tags estão atualizadas
        this.generateFilterTags(this.filterForm.value);
      } else {
        this.hasFilters = false;
        this.appliedFilters = [];
      }
    }
  }

  async filterData() {
    // Verificar se o formulário é válido antes de processar
    if (this.filterForm.invalid) {
      // Marcar todos os campos como touched para mostrar erros
      Object.keys(this.filterForm.controls).forEach((key) => {
        this.filterForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.loading = true;

    try {
      const values = this.filterForm.value;

      const filters: any = {};

      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          // Verificar se o campo tem erro de validação antes de incluir
          const control = this.filterForm.get(key);
          if (control && control.valid) {
            let newValue = value;
            if (key === 'start_date' || key === 'end_date') {
              newValue = value.split('/').reverse().join('-');
            }
            filters[key] = newValue;
          }
        }
      });

      if (filters['start_date'] && !filters['end_date']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['end_date'] = atualDate;
      }

      if (filters['end_date'] && !filters['start_date']) {
        filters['start_date'] = '2000-01-01';
      }

      const { results, count, offset } = await this.api.get({
        route: 'api/v2/user-profile/',
        token: true,
        params: {
          type: 'list',
          page: 1,
          ...filters,
        },
      });

      // ✅ IMPORTANTE: hasFilters só é atualizado após aplicar os filtros com sucesso
      this.hasFilters = Object.keys(filters).length > 0;

      // ✅ Gerar as tags dos filtros aplicados
      this.generateFilterTags(values);

      this.count = count;
      this.data = results;
      this.offset = offset;
      this.showFilterModal = false;
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
    this.loading = false;
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) => `de_can_${key.split('can').join('').toLowerCase()}_groups`
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }

  setBreadcrumb(): void {
    const canViewPermissions = this.authService.verifyPermission(
      'de_can_view_permissions'
    );
    const canViewGroups =
      this.authService.verifyPermission('de_can_view_groups');
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Permissões',
        url: '/app/permissions',
        clickable: canViewPermissions,
      },
      {
        label: 'Perfis de acessos',
        url: '/app/permissions/groups',
        clickable: false, // Página atual, não precisa ser clicável
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumbs);
  }

  renderProfiles(arr: any[]) {
    const labels = arr.map((item) => item.description);

    const displayLabels =
      labels.length > 3
        ? [...labels.slice(0, 3), `+ ${labels.length - 3} itens`]
        : labels;

    return displayLabels.join(', ');
  }

  toggleStatus(id: number) {
    const finded = this.data.find((group) => group.id === id);
    if (!finded) return;
    this.selectedGroup = finded;
    this.isActive = finded.status === 'Ativo' || finded.status === 'active';
    this.showStatusModal = true;
  }

  async confirmStatusChange() {
    if (!this.selectedGroup) return;

    this.showStatusModal = false;
    this.showLoadingSpinner = true; // Mostrar spinner durante a requisição

    try {
      // Determinar o novo status (a API espera active: true/false)
      const currentStatus = this.selectedGroup.status;
      const isCurrentlyActive =
        currentStatus === 'Ativo' || currentStatus === 'active';
      const newActive = !isCurrentlyActive; // Inverter o status atual

      const res: any = await this.api.patch({
        route: `api/v2/user-profile/${this.selectedGroup.id}/`,
        token: true,
        body: {
          active: newActive,
        },
      });

      // Verificar se a resposta indica sucesso
      const isSuccess =
        res && (res.active !== undefined || res.message || res.id);

      if (isSuccess) {
        this.toast.show(
          'info',
          'Sucesso',
          res.message ||
            `Perfil de usuário ${
              this.isActive ? 'inativado' : 'ativado'
            } com sucesso.`
        );
      } else {
        this.toast.show(
          'error',
          'Erro',
          'Ocorreu um erro ao alterar o status do perfil de usuários.'
        );
      }

      this.selectedGroup = null;
      this.isActive = false;
      this.showLoadingSpinner = false; // Esconder spinner antes de chamar getData
      await this.getData(); // Recarregar dados (vai mostrar skeleton)
    } catch (error: any) {
      console.error(error);

      const errorResponse = error?.error || error;
      const isSuccessInError =
        errorResponse &&
        errorResponse.message &&
        (errorResponse.active !== undefined || errorResponse.id);

      if (isSuccessInError) {
        this.toast.show(
          'info',
          'Sucesso',
          errorResponse.message ||
            `Perfil de usuário ${
              this.isActive ? 'inativado' : 'ativado'
            } com sucesso.`
        );
        this.selectedGroup = null;
        this.isActive = false;
        this.showLoadingSpinner = false;
        await this.getData();
      } else {
        const errorMessage =
          error?.error?.message ||
          error?.message ||
          'Ocorreu um erro ao alterar o status do perfil de usuários.';
        this.toast.show('error', 'Erro', errorMessage);
        this.showLoadingSpinner = false; // Esconder spinner em caso de erro real
      }
    }
  }

  closeStatusModal() {
    this.showStatusModal = false;
    this.selectedGroup = null;
    this.isActive = false;
  }

  getModalTitle(): string {
    return this.isActive
      ? 'Inativar perfil de usuário'
      : 'Ativar perfil de usuário';
  }

  getModalMessage(): string {
    return `Deseja ${
      this.isActive ? 'inativar' : 'ativar'
    } esse perfil de usuário? Todas as permissões do usuário serão mantidas.`;
  }

  getConfirmText(): string {
    return `Sim, ${this.isActive ? 'inativar' : 'ativar'} perfil de usuário`;
  }

  async refreshData() {
    await this.getData();
  }

  displayDateTime(dateStr: string) {
    if (!dateStr) return { date: '-', time: '' };

    try {
      // Se já vem no formato "DD/MM/YYYY HH:MM"
      if (dateStr.includes('/') && dateStr.includes(' ')) {
        const [datePart, timePart] = dateStr.split(' ');
        const [day, month, year] = datePart.split('/');
        const [hours, minutes] = timePart.split(':');

        return {
          date: `${day}/${month}/${year}`,
          time: `às ${hours}:${minutes}`,
        };
      }

      // Se for ISO datetime
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return {
        date: `${day}/${month}/${year}`,
        time: `às ${hours}:${minutes}`,
      };
    } catch (error) {
      return { date: dateStr || '-', time: '' };
    }
  }

  /**
   * Verifica se os filtros podem ser aplicados
   * Retorna false se houver campos inválidos ou se não houver nenhum filtro preenchido
   */
  get canApplyFilters(): boolean {
    // Verifica se o formulário é válido
    if (this.filterForm.invalid) {
      return false;
    }

    // Verifica se há pelo menos um filtro preenchido
    const values = this.filterForm.value;
    const hasAnyFilter = Object.values(values).some(
      (value) => value !== null && value !== '' && value !== undefined
    );

    return hasAnyFilter;
  }
}
